// =============================================================================
// DocumentEffectiveSecurityView.jsx — KlassDoc Sprint 10C Session S10C-F
// -----------------------------------------------------------------------------
// Three-mode document security editor + read-only fallback.
//
// MODES (per H10 Phase 2 / ADR-008-A)
//   inherit  — default. Folder ACL is authoritative. Body shows the
//              inherited permissions read-only.
//   restrict — narrow the folder ACL. Only listed users can access,
//              even if others have folder access. Cannot widen.
//   custom   — fully explicit ACL. Folder ACL is ignored. Can add
//              users who don't have folder access.
//
// EDIT AUTHORIZATION
//   Admin OR user with `full` access on the top-level folder OR the
//   document's creator. The backend's GET /:id/security returns
//   `current_user_can_edit` which we trust — this modal ONLY renders
//   the editable shell when that flag is true. Everyone else sees the
//   read-only effective view with a "Manage at folder level" button
//   pointing to the folder's security modal (existing H10 Part B2 UX).
//
// PROPS
//   doc              — { id, name, folder_id, _access_level?, ... }
//   onClose          — close the modal
//   onOpenFolderSecurity(topLevelFolder) — optional; opens the folder
//                      security modal for admins who don't own this doc
//                      but want to manage the parent folder instead
//   onSuccess        — called after a successful PUT (parent re-fetches
//                      the doc)
//   api, currentUser, isAdmin, lang, THEME
// =============================================================================

import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as lucide from "lucide-react";
import { PrincipalPicker, AclEntryRow } from "./security/SecurityControls";

function fmt(lang, fr, en) { return lang === "fr" ? fr : en; }
function T(lang, fr, en) { return lang === "fr" ? fr : en; }

export default function DocumentEffectiveSecurityView({ doc, onClose, onOpenFolderSecurity, onSuccess, api, currentUser, isAdmin, lang = "fr", THEME }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Data loaded from GET /:id/security
  const [securityMode, setSecurityMode] = useState("inherit");
  const [topFolder, setTopFolder] = useState(null);
  const [folderName, setFolderName] = useState(null);
  const [folderEntries, setFolderEntries] = useState([]); // for inherit mode display
  const [docEntries, setDocEntries] = useState([]);       // for restrict/custom modes
  const [canEdit, setCanEdit] = useState(false);

  // Local working state — only diverges from loaded when user edits
  const [workingMode, setWorkingMode] = useState("inherit");
  const [workingEntries, setWorkingEntries] = useState([]);

  // Mode change warning banner — shown after a mode toggle until user acts
  const [modeWarning, setModeWarning] = useState(null);

  async function load() {
    if (!doc?.id) return;
    setLoading(true); setError(null);
    try {
      // GET /:id/security returns everything we need in one round-trip
      const sec = await api.get(`/documents/${doc.id}/security`);
      setSecurityMode(sec.security_mode || "inherit");
      setWorkingMode(sec.security_mode || "inherit");
      setDocEntries(sec.entries || []);
      setWorkingEntries(sec.entries || []);
      setCanEdit(!!sec.current_user_can_edit);
      setFolderName(sec.folder_name || null);

      if (sec.top_level_folder_id) {
        setTopFolder({ id: sec.top_level_folder_id, name: sec.top_level_folder_name });
        // For inherit mode we want to SHOW the inherited entries read-only
        if (sec.security_mode === "inherit" || !sec.security_mode) {
          try {
            const folderAcl = await api.get(`/permissions/folder/${sec.top_level_folder_id}`);
            setFolderEntries((folderAcl.data || []).map(e => ({
              principal_type: e.principal_type,
              principal_id: e.principal_id,
              principal_name: e.principal_name,
              access_level: e.access_level,
              locked: true,
            })));
          } catch {
            // Non-fatal — fallback to empty list
            setFolderEntries([]);
          }
        }
      }
    } catch (e) {
      console.error("Load doc security:", e);
      setError(e?.message || fmt(lang, "Échec du chargement", "Failed to load"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [doc?.id]); // eslint-disable-line

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !saving) onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const dirty = workingMode !== securityMode
    || JSON.stringify(workingEntries.map(e => ({ t: e.principal_type, i: e.principal_id, a: e.access_level })).sort())
       !== JSON.stringify(docEntries.map(e => ({ t: e.principal_type, i: e.principal_id, a: e.access_level })).sort());

  function switchMode(newMode) {
    if (newMode === workingMode) return;
    // Warning banner by destination
    const warn = (() => {
      if (workingMode === "inherit" && newMode !== "inherit") return T(lang, "docSecSwitchRestrictWarn", "docSecSwitchRestrictWarn");
      if (newMode === "inherit") return T(lang, "docSecSwitchInheritWarn", "docSecSwitchInheritWarn");
      if (workingMode === "restrict" && newMode === "custom") return T(lang, "docSecSwitchCustomWarn", "docSecSwitchCustomWarn");
      if (workingMode === "custom" && newMode === "restrict") return T(lang, "docSecSwitchCustomWarn", "docSecSwitchCustomWarn");
      return null;
    })();
    setModeWarning(warn);

    if (newMode === "inherit") {
      setWorkingEntries([]);
    } else if (workingMode === "inherit" && newMode !== "inherit") {
      // Seed working entries from the inherited folder entries so the user
      // has a starting point (plus creator + admin group — backend will
      // inject these anyway, but showing them immediately is friendlier).
      const seeded = [...folderEntries.map(e => ({ ...e, locked: false }))];
      // Ensure creator is in there
      if (currentUser?.id && !seeded.some(e => e.principal_type === "user" && e.principal_id === currentUser.id)) {
        seeded.unshift({
          principal_type: "user",
          principal_id: currentUser.id,
          principal_name: currentUser.name || "You",
          access_level: "full",
          locked: true,
        });
      } else {
        seeded.forEach(e => { if (e.principal_type === "user" && e.principal_id === currentUser?.id) e.locked = true; });
      }
      setWorkingEntries(seeded);
    }
    setWorkingMode(newMode);
  }

  function addPrincipal(p) {
    setWorkingEntries(prev => {
      if (prev.some(e => e.principal_type === p.principal_type && e.principal_id === p.principal_id)) return prev;
      return [...prev, p];
    });
  }
  function updateLevel(idx, level) {
    setWorkingEntries(prev => prev.map((e, i) => i === idx ? { ...e, access_level: level } : e));
  }
  function removePrincipal(idx) {
    setWorkingEntries(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    if (!canEdit || saving || !dirty) return;
    setSaving(true); setError(null);
    try {
      const payload = workingMode === "inherit"
        ? { security_mode: "inherit", entries: [] }
        : {
            security_mode: workingMode,
            entries: workingEntries
              .filter(e => e.principal_id)
              .map(e => ({
                principal_type: e.principal_type,
                principal_id: e.principal_id,
                principal_name: e.principal_name,
                access_level: e.access_level,
              })),
          };
      await api.put(`/documents/${doc.id}/security`, payload);
      onSuccess?.();
      onClose();
    } catch (e) {
      console.error("Save doc security:", e);
      setError(e?.message || fmt(lang, "Échec de l'enregistrement", "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const excludeIds = useMemo(() =>
    new Set(workingEntries.map(e => `${e.principal_type}-${e.principal_id}`)),
  [workingEntries]);

  const FONT = "'DM Sans','Satoshi',-apple-system,sans-serif";
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 };

  // ── Mode card (segmented control) ──
  const ModeCard = ({ id, icon: Icon, color, title, desc }) => {
    const active = workingMode === id;
    const disabled = !canEdit;
    return (
      <button
        type="button"
        onClick={() => !disabled && switchMode(id)}
        disabled={disabled}
        style={{
          flex: 1, minWidth: 0, textAlign: "left", padding: 14, borderRadius: 10,
          background: active ? color + "18" : THEME.surface,
          border: active ? `1.5px solid ${color}` : `1px solid ${THEME.borderSubtle}`,
          cursor: disabled ? "not-allowed" : "pointer",
          fontFamily: FONT, transition: "all 0.15s", opacity: disabled && !active ? 0.5 : 1,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <Icon size={16} color={active ? color : THEME.textMuted} />
          <span style={{ fontSize: 13, fontWeight: 700, color: active ? color : THEME.text }}>{title}</span>
        </div>
        <div style={{ fontSize: 11, color: THEME.textDim, lineHeight: 1.5 }}>{desc}</div>
      </button>
    );
  };

  // Which entries are displayed in the list?
  const displayEntries = workingMode === "inherit" ? folderEntries : workingEntries;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }}
      onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
        style={{ background: THEME.surfaceRaised, border: `1px solid ${THEME.border}`, borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 32px 96px rgba(0,0,0,0.6)" }}
      >
        {/* HEADER */}
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 11, background: `linear-gradient(135deg, ${THEME.warning}, ${THEME.warning}CC)`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 16px ${THEME.warning}40` }}>
              <lucide.Shield size={22} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: THEME.text }}>
                {T(lang, "Sécurité du document", "Document Security")}
              </div>
              <div style={{ fontSize: 12, color: THEME.textDim, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
                <span>{doc?.name}</span>
                {!canEdit && (
                  <span style={{ padding: "2px 8px", borderRadius: 4, background: THEME.infoDim, color: THEME.info, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                    {T(lang, "Lecture seule", "Read-only")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={() => !saving && onClose()} disabled={saving} style={{ background: "transparent", border: "none", cursor: saving ? "not-allowed" : "pointer", padding: 6 }}>
            <lucide.X size={20} color={THEME.textMuted} />
          </button>
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 28px" }}>
          {error && (
            <div style={{ padding: "10px 14px", marginBottom: 14, background: THEME.dangerDim, border: `1px solid ${THEME.danger}40`, borderRadius: 8, color: THEME.danger, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <lucide.AlertCircle size={14} />{error}
            </div>
          )}

          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, gap: 10 }}>
              <lucide.Loader2 size={22} color={THEME.accent} className="spin" />
              <span style={{ fontSize: 13, color: THEME.textDim }}>{T(lang, "Chargement...", "Loading...")}</span>
            </div>
          ) : (
            <>
              {/* Mode toggle — three cards */}
              <div style={labelStyle}>{T(lang, "Mode de sécurité", "Security mode")}</div>
              <div style={{ display: "flex", gap: 10, marginBottom: modeWarning ? 14 : 22 }}>
                <ModeCard id="inherit"  icon={lucide.FolderOpen} color={THEME.info}
                          title={T(lang, "Hériter du dossier", "Inherit from folder")}
                          desc={T(lang, "Utilise les permissions du dossier.", "Uses the folder's permissions.")} />
                <ModeCard id="restrict" icon={lucide.Filter}     color={THEME.warning}
                          title={T(lang, "Restreindre", "Restrict")}
                          desc={T(lang, "Réduit l'ACL du dossier à une liste plus courte.", "Narrows the folder ACL to a shorter list.")} />
                <ModeCard id="custom"   icon={lucide.Lock}       color={THEME.accent}
                          title={T(lang, "Personnalisé", "Custom")}
                          desc={T(lang, "ACL indépendante — peut ajouter des utilisateurs hors du dossier.", "Standalone ACL — can add users outside the folder.")} />
              </div>

              {/* Mode-change warning */}
              {modeWarning && (
                <div style={{ padding: 12, marginBottom: 18, background: THEME.warningDim, border: `1px solid ${THEME.warning}40`, borderRadius: 8, color: THEME.text, fontSize: 12, display: "flex", gap: 10 }}>
                  <lucide.AlertTriangle size={14} color={THEME.warning} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>{modeWarning}</span>
                </div>
              )}

              {/* Mode-specific description + body */}
              {workingMode === "inherit" && (
                <div>
                  <div style={{ padding: 12, marginBottom: 16, background: THEME.infoDim, border: `1px solid ${THEME.info}40`, borderRadius: 8, fontSize: 12, color: THEME.textDim, lineHeight: 1.55, display: "flex", gap: 10 }}>
                    <lucide.Info size={14} color={THEME.info} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      {T(lang, "Ce document utilise les permissions du dossier ", "This document uses the folder's permissions from ")}
                      <strong style={{ color: THEME.text }}>{topFolder?.name || folderName || T(lang, "le dossier racine", "the top-level folder")}</strong>
                      {T(lang, ". Tous les utilisateurs ayant accès au dossier peuvent le voir.", ". Every user with folder access can see it.")}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{folderEntries.length} {T(lang, "principaux (hérités)", "principals (inherited)")}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 4, color: THEME.info }}>
                      <lucide.Eye size={11} /> {T(lang, "Lecture seule", "Read-only")}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {folderEntries.length === 0 ? (
                      <div style={{ padding: 24, textAlign: "center", color: THEME.textDim, fontSize: 12 }}>
                        {T(lang, "Aucune permission définie sur le dossier — seuls les administrateurs ont accès.", "No permissions defined on the folder — only administrators have access.")}
                      </div>
                    ) : folderEntries.map((entry, idx) => (
                      <AclEntryRow
                        key={`${entry.principal_type}-${entry.principal_id}-${idx}`}
                        entry={entry}
                        onChangeAccess={() => {}}
                        onRemove={() => {}}
                        lang={lang}
                        THEME={THEME}
                        readOnly={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {(workingMode === "restrict" || workingMode === "custom") && (
                <div>
                  <div style={{ padding: 12, marginBottom: 16, background: workingMode === "restrict" ? THEME.warningDim : THEME.accentDim, border: `1px solid ${(workingMode === "restrict" ? THEME.warning : THEME.accent)}40`, borderRadius: 8, fontSize: 12, color: THEME.textDim, lineHeight: 1.55, display: "flex", gap: 10 }}>
                    <lucide.Info size={14} color={workingMode === "restrict" ? THEME.warning : THEME.accent} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                      {workingMode === "restrict"
                        ? T(lang, "Mode restreint : seuls les utilisateurs de la liste ci-dessous peuvent voir ce document, même s'ils ont accès au dossier.", "Restrict mode: only the users below can see this document, even if they have folder access.")
                        : T(lang, "Mode personnalisé : ce document n'utilise plus les permissions du dossier. Vous pouvez accorder l'accès à des utilisateurs hors du dossier.", "Custom mode: this document no longer uses folder permissions. You can grant access to users outside the folder.")}
                    </span>
                  </div>

                  {canEdit && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={labelStyle}>{T(lang, "Ajouter des utilisateurs/groupes", "Add Users/Groups")}</label>
                      <PrincipalPicker api={api} lang={lang} THEME={THEME} onAdd={addPrincipal} excludeIds={excludeIds} />
                    </div>
                  )}

                  <div style={{ fontSize: 11, fontWeight: 600, color: THEME.textMuted, marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.3 }}>
                    {displayEntries.length} {T(lang, "principaux avec accès", "principals with access")}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {displayEntries.length === 0 ? (
                      <div style={{ padding: 24, textAlign: "center", color: THEME.textDim, fontSize: 12 }}>
                        {T(lang, "Aucune permission — seul le créateur et les administrateurs verront ce document.", "No permissions set — only the creator and administrators will see this document.")}
                      </div>
                    ) : displayEntries.map((entry, idx) => (
                      <AclEntryRow
                        key={`${entry.principal_type}-${entry.principal_id}-${idx}`}
                        entry={entry}
                        onChangeAccess={lvl => updateLevel(idx, lvl)}
                        onRemove={() => removePrincipal(idx)}
                        lang={lang}
                        THEME={THEME}
                        readOnly={!canEdit}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div style={{ padding: "14px 28px", borderTop: `1px solid ${THEME.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", background: THEME.surface }}>
          <div style={{ fontSize: 11, color: THEME.textDim, display: "flex", alignItems: "center", gap: 6 }}>
            <lucide.Lock size={11} />
            {T(lang, "L'auteur et le groupe Administrateurs conservent toujours l'accès", "Author and Administrators group always retain access")}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" onClick={onClose} disabled={saving} style={{ padding: "10px 20px", background: "transparent", color: THEME.textMuted, border: `1px solid ${THEME.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", fontFamily: FONT }}>
              {canEdit ? T(lang, "Annuler", "Cancel") : T(lang, "Fermer", "Close")}
            </button>
            {!canEdit && topFolder && onOpenFolderSecurity && (
              <button type="button" onClick={() => onOpenFolderSecurity(topFolder)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", background: `linear-gradient(135deg, ${THEME.warning}, ${THEME.warning}CC)`, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: `0 4px 16px ${THEME.warning}40`, fontFamily: FONT }}>
                <lucide.Shield size={15} />
                {T(lang, "Gérer au niveau du dossier", "Manage at folder level")}
              </button>
            )}
            {canEdit && (
              <button type="button" onClick={handleSave} disabled={saving || !dirty || loading} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px", background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentHover})`, color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: (saving || !dirty || loading) ? "not-allowed" : "pointer", boxShadow: `0 4px 16px ${THEME.accent}30`, fontFamily: FONT, opacity: (saving || !dirty || loading) ? 0.6 : 1 }}>
                {saving ? <lucide.Loader2 size={15} className="spin" /> : <lucide.Save size={15} />}
                {saving ? T(lang, "Enregistrement...", "Saving...") : T(lang, "Enregistrer", "Save")}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
